import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Viral Loops types
interface VLUser {
  email: string;
  firstname?: string;
  lastname?: string;
  extraData?: Record<string, string>;
}

interface VLIdentifyResponse {
  referralCode: string;
  referralUrls: {
    email: string;
    facebook: string;
    twitter: string;
    linkedin: string;
    whatsapp: string;
    telegram: string;
    referralUrl: string;
  };
}

interface CampaignPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

type PopupStage = "loading" | "join" | "share";

interface JoinStageFormProps {
  onSuccess: (referralData: VLIdentifyResponse) => void;
}

const JoinStageForm: React.FC<JoinStageFormProps> = ({ onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Get form data
      const formData = new FormData(formRef.current!);
      const email = formData.get("email") as string;
      const screenName = formData.get("screenName") as string;
      const walletAddress = formData.get("walletAddress") as string;
      const insecurityQuestion = formData.get("insecurityQuestion") as string;
      const insecurityAnswer = formData.get("insecurityAnswer") as string;

      // Wait for Viral Loops SDK
      const campaign = await (window as any).ViralLoops?.getCampaign();
      if (!campaign) {
        throw new Error("Campaign not loaded");
      }

      // Identify user with Viral Loops
      const user: VLUser = {
        email,
        extraData: {
          screenName: screenName || "",
          walletAddress: walletAddress || "",
          insecurityQuestion: insecurityQuestion || "",
          insecurityAnswer: insecurityAnswer || "",
        },
      };

      const response = await campaign.identify(user);
      onSuccess(response);
    } catch (err) {
      console.error("Failed to join campaign:", err);
      setError("Failed to join. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <motion.h2
          className="text-2xl sm:text-3xl font-black"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <span className="text-transparent bg-clip-text bg-gradient-to-br from-yellow-400 via-amber-400 to-orange-500">
            GO AWAY, GET OUT
          </span>
        </motion.h2>
        <p className="text-gray-400 text-sm">
          You think you'll get early access or rewards or something?
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="text-red-400 text-sm text-center bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-2">
          {error}
        </div>
      )}

      {/* Form */}
      <form ref={formRef} className="space-y-4" onSubmit={handleSubmit}>
        {/* Email - Required */}
        <div>
          <label className="block text-xs font-bold tracking-wider text-amber-400/80 mb-2">
            EMAIL *
          </label>
          <input
            type="email"
            name="email"
            required
            placeholder="tony_hawks_pro_skater2@hotmail.com"
            onInvalid={(e) => {
              const target = e.target as HTMLInputElement;
              if (target.validity.valueMissing) {
                target.setCustomValidity(
                  "Missing required field. 10/10 perfect."
                );
              } else if (target.validity.typeMismatch) {
                target.setCustomValidity("Skill issue");
              }
            }}
            onInput={(e) =>
              (e.target as HTMLInputElement).setCustomValidity("")
            }
            className="w-full px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white placeholder-gray-500
              focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
              focus:outline-none transition-all duration-300"
          />
        </div>

        {/* Screen Name - Optional */}
        <div>
          <label className="block text-xs font-bold tracking-wider text-gray-400 mb-2">
            SCREEN NAME{" "}
            <span className="text-[10px] font-normal">OPTIONAL</span>
          </label>
          <input
            type="text"
            name="screenName"
            placeholder="xX_Sh4d0wSn1p3R_Xx"
            className="w-full px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white placeholder-gray-500
              focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
              focus:outline-none transition-all duration-300"
          />
        </div>

        {/* Wallet Address - Optional */}
        <div>
          <label className="block text-xs font-bold tracking-wider text-gray-400 mb-2">
            WALLET ADDRESS{" "}
            <span className="text-[10px] font-normal">OPTIONAL</span>
          </label>
          <input
            type="text"
            name="walletAddress"
            placeholder="0x..."
            className="w-full px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white placeholder-gray-500 font-mono text-sm
              focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
              focus:outline-none transition-all duration-300"
          />
        </div>

        {/* Insecurity Question - Optional */}
        <div>
          <label className="block text-xs font-bold tracking-wider text-gray-400 mb-2">
            INSECURITY QUESTION
          </label>
          <select
            name="insecurityQuestion"
            className="w-full px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white
              focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
              focus:outline-none transition-all duration-300"
          >
            <option value="" className="bg-gray-900">
              Select a question...
            </option>
            <option
              value="What talent did you give up on too early?"
              className="bg-gray-900"
            >
              What talent did you give up on too early?
            </option>
            <option
              value="What event are you still waiting to be invited to?"
              className="bg-gray-900"
            >
              What event are you still waiting to be invited to?
            </option>
            <option
              value="Why haven't they approved your follow request?"
              className="bg-gray-900"
            >
              Why haven't they approved your follow request?
            </option>
            <option
              value="What part of yourself did you kill to fit in?"
              className="bg-gray-900"
            >
              What part of yourself did you kill to fit in?
            </option>
            <option
              value="What's the soulmate you drove away doing now?"
              className="bg-gray-900"
            >
              What's the soulmate you drove away doing now?
            </option>
            <option
              value="How many years has it been since you peaked?"
              className="bg-gray-900"
            >
              How many years has it been since you peaked?
            </option>
            <option
              value="If you're so smart why aren't you rich yet?"
              className="bg-gray-900"
            >
              If you're so smart why aren't you rich yet?
            </option>
          </select>
        </div>

        {/* Answer - Optional */}
        <div>
          <label className="block text-xs font-bold tracking-wider text-gray-400 mb-2">
            ANSWER <span className="text-[10px] font-normal">OPTIONAL</span>
          </label>
          <input
            type="text"
            name="insecurityAnswer"
            placeholder="..."
            className="w-full px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white placeholder-gray-500
              focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/30
              focus:outline-none transition-all duration-300"
          />
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          className="group relative w-full disabled:opacity-70"
          whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        >
          {/* Button glow */}
          <motion.div
            className="absolute inset-0 rounded-xl bg-gradient-to-r from-amber-600 to-red-600 blur-lg opacity-50 group-hover:opacity-80"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          {/* Button */}
          <div className="relative px-6 py-4 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 rounded-xl overflow-hidden">
            {/* Shimmer */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
              animate={{ x: ["-200%", "200%"] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
            />
            <span className="relative text-white font-black text-lg tracking-wider">
              {isSubmitting ? "LMAO..." : "IDK LOL"}
            </span>
          </div>
        </motion.button>
      </form>
    </div>
  );
};

interface ShareStageProps {
  onClose: () => void;
  referralData: VLIdentifyResponse;
}

const ShareStage: React.FC<ShareStageProps> = ({ onClose, referralData }) => {
  const [copied, setCopied] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const referralLink =
    referralData.referralUrls?.referralUrl ||
    `https://vrlps.co/${referralData.referralCode}`;

  const handleNftClick = () => {
    setShowTermsPopup(true);
    setTermsAccepted(false);
  };

  const handleProceedToNft = () => {
    if (termsAccepted) {
      window.open("https://manifold.xyz/@taulpaul/id/4102613232", "_blank");
      setShowTermsPopup(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = referralLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToTwitter = () => {
    // Use the pre-built Twitter share URL if available
    if (referralData.referralUrls?.twitter) {
      window.open(referralData.referralUrls.twitter, "_blank");
    } else {
      const text = encodeURIComponent(
        "Just joined the 1V1ME waitlist. Tournament-style token battles coming Winter 2026. Get in loser."
      );
      const url = encodeURIComponent(referralLink);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
        "_blank"
      );
    }
  };

  const shareToTelegram = () => {
    // Use the pre-built Telegram share URL if available
    if (referralData.referralUrls?.telegram) {
      window.open(referralData.referralUrls.telegram, "_blank");
    } else {
      const text = encodeURIComponent(
        "Just joined the 1V1ME waitlist. Tournament-style token battles. Get in:"
      );
      const url = encodeURIComponent(referralLink);
      window.open(`https://t.me/share/url?url=${url}&text=${text}`, "_blank");
    }
  };

  return (
    <div className="space-y-6 text-center">
      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center"
      >
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={3}
            d="M5 13l4 4L19 7"
          />
        </svg>
      </motion.div>

      {/* Success Message */}
      <div className="space-y-2">
        <motion.h2
          className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-br from-green-400 to-emerald-500"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          RELAX. YOU'RE IN, OKAY?
        </motion.h2>
        <p className="text-gray-400 text-sm">
          Just don't bother anyone else about it for more points
        </p>
      </div>

      {/* Referral Link */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="space-y-3"
      >
        <label className="block text-xs font-bold tracking-wider text-amber-400/80">
          YOUR REFERRAL LINK
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={referralLink || "Loading..."}
            className="flex-1 px-4 py-3 bg-black/40 border border-gray-700/50 rounded-xl
              text-white text-sm font-mono truncate
              focus:outline-none"
          />
          <motion.button
            onClick={handleCopy}
            className={`px-4 py-3 rounded-xl font-bold text-sm transition-all ${
              copied
                ? "bg-green-500 text-white"
                : "bg-gray-800 text-gray-300 hover:bg-gray-700"
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {copied ? "COPIED!" : "COPY"}
          </motion.button>
        </div>
      </motion.div>

      {/* Social Share Buttons */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-3"
      >
        <p className="text-xs text-gray-500 tracking-wider">
          OR SHARE DIRECTLY
        </p>
        <div className="flex justify-center gap-3">
          {/* Twitter/X */}
          <motion.button
            onClick={shareToTwitter}
            className="flex items-center gap-2 px-5 py-3 bg-black border border-gray-700 rounded-xl
              text-white font-bold text-sm hover:border-gray-500 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            X
          </motion.button>

          {/* Telegram */}
          <motion.button
            onClick={shareToTelegram}
            className="flex items-center gap-2 px-5 py-3 bg-[#229ED9] rounded-xl
              text-white font-bold text-sm hover:bg-[#1a8bc7] transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
            </svg>
            Telegram
          </motion.button>
        </div>
      </motion.div>

      {/* NFT Link */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="space-y-3"
      >
        <p className="text-xs text-gray-500 tracking-wider">
          OR BUY AN NFT LOL
        </p>
        <motion.button
          onClick={handleNftClick}
          className="w-full px-5 py-3 bg-purple-600/20 border border-purple-500/30 rounded-xl
            text-purple-300 font-medium text-sm hover:bg-purple-600/30 hover:border-purple-500/50 transition-all"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          1V1ME FOUNDING PASS
        </motion.button>
      </motion.div>

      {/* Terms Popup */}
      <AnimatePresence>
        {showTermsPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/70"
              onClick={() => setShowTermsPopup(false)}
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-gray-900 border border-purple-500/30 rounded-2xl p-6 max-w-sm w-full space-y-4"
            >
              <h3 className="text-lg font-bold text-purple-300">
                Hold up, wait a minute
              </h3>
              <p className="text-gray-400 text-sm">
                You're about to leave and visit Manifold to purchase an NFT.
                Please review our{" "}
                <a
                  href="/nftsaleterms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 underline hover:text-purple-300"
                >
                  NFT sale terms
                </a>{" "}
                and{" "}
                <a
                  href="/tos.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-400 underline hover:text-purple-300"
                >
                  TOS
                </a>
                .
              </p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-800 text-purple-500 focus:ring-purple-500"
                />
                <span className="text-gray-300 text-sm">
                  I accept the sale terms and terms of use.
                </span>
              </label>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowTermsPopup(false)}
                  className="flex-1 px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedToNft}
                  disabled={!termsAccepted}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium text-sm transition-all ${
                    termsAccepted
                      ? "bg-purple-600 text-white hover:bg-purple-500"
                      : "bg-gray-700 text-gray-500 cursor-not-allowed"
                  }`}
                >
                  Proceed
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Close Button */}
      <motion.button
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="text-gray-500 hover:text-white transition-colors text-sm"
        whileHover={{ scale: 1.05 }}
      >
        This was a mistake →
      </motion.button>
    </div>
  );
};

export const CampaignPopup: React.FC<CampaignPopupProps> = ({
  isOpen,
  onClose,
}) => {
  const [stage, setStage] = useState<PopupStage>("loading");
  const [referralData, setReferralData] = useState<VLIdentifyResponse | null>(
    null
  );

  // Check if user already exists when popup opens
  useEffect(() => {
    if (!isOpen) return;

    const checkExistingUser = async () => {
      try {
        const campaign = await (window as any).ViralLoops?.getCampaign();
        if (campaign) {
          const user = await campaign.getUser();
          // getUser() returns user data with referralCode if they exist
          if (user && user.referralCode) {
            // User already joined - construct referral data from user object
            const referralData: VLIdentifyResponse = {
              referralCode: user.referralCode,
              referralUrls: user.referralUrls || {
                referralUrl: `https://vrlps.co/${user.referralCode}`,
                email: "",
                facebook: "",
                twitter: "",
                linkedin: "",
                whatsapp: "",
                telegram: "",
              },
            };
            setReferralData(referralData);
            setStage("share");
            return;
          }
        }
      } catch (err) {
        console.error("Failed to check existing user:", err);
      }
      // No existing user - show join stage
      setStage("join");
    };

    checkExistingUser();
  }, [isOpen]);

  // Handle successful join
  const handleJoinSuccess = (data: VLIdentifyResponse) => {
    setReferralData(data);
    setStage("share");
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Card */}
          <motion.div
            className="relative w-full max-w-md"
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-red-500/20 rounded-3xl blur-xl" />

            {/* Card content */}
            <div className="relative bg-black/90 backdrop-blur-xl border border-gray-800/50 rounded-3xl p-8">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-500 hover:text-white transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <AnimatePresence mode="wait">
                {stage === "loading" && (
                  <motion.div
                    key="loading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center justify-center py-12"
                  >
                    <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  </motion.div>
                )}
                {stage === "join" && (
                  <motion.div
                    key="join"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <JoinStageForm onSuccess={handleJoinSuccess} />
                  </motion.div>
                )}
                {stage === "share" && referralData && (
                  <motion.div
                    key="share"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ShareStage onClose={onClose} referralData={referralData} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
